import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  CompleteContractFileUploadBody,
  CompleteContractFileUploadV1ContractsContractIdFilesCompleteUploadPostParams,
  CompleteContractNoteFilesBody,
  CompleteContractNoteFilesV1ContractsContractIdNotesNoteIdCompleteFilesPostParams,
  CompleteContractUploadBody,
  CompleteContractUploadV1ContractsContractIdCompleteUploadPostParams,
  CompletePartyNoteFilesBody,
  CompletePartyNoteFilesV1ContractsPartiesPartyIdNotesNoteIdCompleteFilesPostParams,
  ContractExportReportFormat,
  CreateContractContributorBody,
  CreateContractContributorV1ContractsContractIdContributorsPostParams,
  CreateContractNoteBody,
  CreateContractNoteV1ContractsContractIdNotesPostParams,
  CreateContractPartyRequest,
  CreateContractRequest,
  CreateJurisdictionRequest,
  CreatePartyContactRequest,
  CreatePartyNoteBody,
  CreatePartyNoteV1ContractsPartiesPartyIdNotesPostParams,
  CreatePartyRequest,
  CreatePolicyCoverageRequest,
  DeleteContractContributorV1ContractsContractIdContributorsContributorIdDeleteParams,
  DeleteContractFileByContractV1ContractsContractIdFilesFileIdDeleteParams,
  DeleteContractNoteFileV1ContractsContractIdNotesNoteIdFilesStorageItemIdDeleteParams,
  DeleteContractNoteV1ContractsContractIdNotesNoteIdDeleteParams,
  DeletePartyNoteV1ContractsPartiesPartyIdNotesNoteIdDeleteParams,
  GenerateContractExportReportRequest,
  GenerateContractExportZipRequest,
  GetContractContributorsResponse,
  GetContractContributorsV1ContractsContractIdContributorsGetParams,
  GetContractFilesListResponse,
  GetContractFilesV1ContractsContractFilesGetParams,
  GetContractNotesResponse,
  GetContractNotesV1ContractsContractIdNotesGetParams,
  GetContractPartiesDetailsResponse,
  GetContractPartiesListResponse,
  GetContractPartiesV1ContractsContractPartiesGetParams,
  GetContractPartyResponse,
  GetContractResponse,
  GetContractsListResponse,
  GetContractsV1ContractsGetParams,
  GetJurisdictionResponse,
  GetJurisdictionsListResponse,
  GetJurisdictionsV1ContractsJurisdictionsGetParams,
  GetPartiesListResponse,
  GetPartiesMinimalListResponse,
  GetPartiesMinimalV1ContractsPartiesMinimalGetParams,
  GetPartiesV1ContractsPartiesGetParams,
  GetPartyContactResponse,
  GetPartyContactsListResponse,
  GetPartyContactsV1ContractsPartyContactsGetParams,
  GetPartyNoteResponse,
  GetPartyNoteV1ContractsPartiesPartyIdNotesNoteIdGetParams,
  GetPartyNotesResponse,
  GetPartyNotesV1ContractsPartiesPartyIdNotesGetParams,
  GetPartyResponse,
  GetPolicyCoverageResponse,
  GetPolicyCoveragesListResponse,
  GetPolicyCoveragesV1ContractsPolicyCoveragesGetParams,
  InitContractFileUploadBody,
  InitContractFileUploadV1ContractsContractIdFilesInitUploadPostParams,
  UpdateContractBody,
  UpdateContractFileBody,
  UpdateContractFileV1ContractsContractIdFilesFileIdPatchParams,
  UpdateContractNoteBody,
  UpdateContractNoteV1ContractsContractIdNotesNoteIdPatchParams,
  UpdateContractPartyBody,
  UpdateContractV1ContractsIdPatchParams,
  UpdateJurisdictionBody,
  UpdatePartyBody,
  UpdatePartyContactBody,
  UpdatePartyNoteBody,
  UpdatePartyNoteV1ContractsPartiesPartyIdNotesNoteIdPatchParams,
  UpdatePolicyCoverageBody,
  UpsertContractContributorBody,
  UpsertContractContributorV1ContractsContractIdContributorsContributorIdPatchParams,
} from 'compliance-api/generated';
import { FormikErrors, FormikValues } from 'formik';
import { toast } from 'react-hot-toast';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { formatApiErrorMessage, withErrorHandling } from '@/modules/common/utils/error-utils.tsx';

export const JURISDICTIONS_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-jurisdictions-list';
export const JURISDICTION_QUERY_KEY = 'vestra-compliance/contract-management-jurisdiction';
export const PARTIES_LIST_QUERY_KEY = 'vestra-compliance/contract-management-parties-list';
export const PARTIES_MINIMAL_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-parties-minimal-list';
export const PARTY_QUERY_KEY = 'vestra-compliance/contract-management-party';
export const PARTY_CONTACTS_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-party-contacts-list';
export const PARTY_CONTACT_QUERY_KEY = 'vestra-compliance/contract-management-party-contact';
export const PARTY_NOTES_QUERY_KEY = 'vestra-compliance/contract-management-party-notes';
export const PARTY_NOTE_QUERY_KEY = 'vestra-compliance/contract-management-party-note';
export const CONTRACT_PARTIES_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-contract-parties-list';
export const CONTRACT_PARTY_QUERY_KEY = 'vestra-compliance/contract-management-contract-party';
export const POLICY_COVERAGES_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-policy-coverages-list';
export const POLICY_COVERAGE_QUERY_KEY = 'vestra-compliance/contract-management-policy-coverage';
export const CONTRACT_FILES_LIST_QUERY_KEY =
  'vestra-compliance/contract-management-contract-files-list';
export const CONTRACTS_LIST_QUERY_KEY = 'vestra-compliance/contract-management-contracts-list';
export const CONTRACT_QUERY_KEY = 'vestra-compliance/contract-management-contract';
export const CONTRACT_PARTIES_DETAILS_QUERY_KEY =
  'vestra-compliance/contract-management-contract-parties-details';
export const CONTRACT_CONTRIBUTORS_QUERY_KEY =
  'vestra-compliance/contract-management-contract-contributors';
export const CONTRACT_NOTES_QUERY_KEY = 'vestra-compliance/contract-management-contract-notes';

type DeletePartyContactMutationPayload = number | { id: number; partyId?: number };
type DeleteContractPartyMutationPayload =
  | number
  | { id: number; contractId?: number; partyId?: number };
type DeletePolicyCoverageMutationPayload = number | { id: number; contractId?: number };

const getPayloadId = (payload: number | { id: number }) =>
  typeof payload === 'number' ? payload : payload.id;

const invalidatePartyQueries = (partyId?: number) => {
  queryClient.invalidateQueries([PARTIES_LIST_QUERY_KEY]);
  queryClient.invalidateQueries([PARTIES_MINIMAL_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(partyId ? [PARTY_QUERY_KEY, partyId] : [PARTY_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACTS_LIST_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_PARTIES_LIST_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_PARTIES_DETAILS_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_PARTY_QUERY_KEY]);
};

const invalidateContractQueries = (contractId?: number) => {
  queryClient.invalidateQueries([CONTRACTS_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(
    contractId ? [CONTRACT_QUERY_KEY, contractId] : [CONTRACT_QUERY_KEY],
  );
  queryClient.invalidateQueries([CONTRACT_PARTIES_LIST_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_PARTIES_DETAILS_QUERY_KEY]);
  queryClient.invalidateQueries([CONTRACT_PARTY_QUERY_KEY]);
};

const invalidateJurisdictionQueries = (jurisdictionId?: number) => {
  queryClient.invalidateQueries([JURISDICTIONS_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(
    jurisdictionId ? [JURISDICTION_QUERY_KEY, jurisdictionId] : [JURISDICTION_QUERY_KEY],
  );
};

const invalidateContractFilesQueries = () => {
  queryClient.invalidateQueries([CONTRACT_FILES_LIST_QUERY_KEY]);
};

const invalidateContractPartiesDetailsQuery = (contractId: number) => {
  queryClient.invalidateQueries([CONTRACT_PARTIES_DETAILS_QUERY_KEY, contractId]);
};

const invalidateContractContributorsQueries = (contractId?: number) => {
  queryClient.invalidateQueries(
    contractId ? [CONTRACT_CONTRIBUTORS_QUERY_KEY, contractId] : [CONTRACT_CONTRIBUTORS_QUERY_KEY],
  );
};

const invalidateContractNotesQuery = (contractId: number) => {
  queryClient.invalidateQueries([CONTRACT_NOTES_QUERY_KEY, contractId]);
};

const invalidatePartyContactsQueries = (contactId?: number, partyId?: number) => {
  queryClient.invalidateQueries([PARTY_CONTACTS_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(
    contactId ? [PARTY_CONTACT_QUERY_KEY, contactId] : [PARTY_CONTACT_QUERY_KEY],
  );
  invalidatePartyQueries(partyId);
  queryClient.invalidateQueries([CONTRACT_PARTIES_DETAILS_QUERY_KEY]);
};

const invalidateContractPartiesQueries = (
  contractPartyId?: number,
  contractId?: number,
  partyId?: number,
) => {
  queryClient.invalidateQueries([CONTRACT_PARTIES_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(
    contractPartyId ? [CONTRACT_PARTY_QUERY_KEY, contractPartyId] : [CONTRACT_PARTY_QUERY_KEY],
  );

  if (contractId) {
    invalidateContractQueries(contractId);
    invalidateContractPartiesDetailsQuery(contractId);
  } else {
    invalidateContractQueries();
    queryClient.invalidateQueries([CONTRACT_PARTIES_DETAILS_QUERY_KEY]);
  }

  invalidatePartyQueries(partyId);
};

const invalidatePolicyCoveragesQueries = (policyCoverageId?: number, contractId?: number) => {
  queryClient.invalidateQueries([POLICY_COVERAGES_LIST_QUERY_KEY]);
  queryClient.invalidateQueries(
    policyCoverageId ? [POLICY_COVERAGE_QUERY_KEY, policyCoverageId] : [POLICY_COVERAGE_QUERY_KEY],
  );
  invalidateContractQueries(contractId);
};

export const useJurisdictionsListQuery = (
  payload: GetJurisdictionsV1ContractsJurisdictionsGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetJurisdictionsListResponse>({
    queryKey: [JURISDICTIONS_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getJurisdictionsV1ContractsJurisdictionsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useJurisdictionQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetJurisdictionResponse>({
    queryKey: [JURISDICTION_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getJurisdictionV1ContractsJurisdictionsIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreateJurisdictionMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreateJurisdictionRequest) => {
      const response = await withErrorHandling(
        api.v1.createJurisdictionV1ContractsJurisdictionsPost(payload),
        {
          loading: 'Creating jurisdiction...',
          success: 'Jurisdiction created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidateJurisdictionQueries(data.jurisdiction.id);
    },
  });
};

export const useUpdateJurisdictionMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateJurisdictionBody }) => {
      const response = await withErrorHandling(
        api.v1.updateJurisdictionV1ContractsJurisdictionsIdPatch({ id }, data),
        {
          loading: 'Updating jurisdiction...',
          success: 'Jurisdiction updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidateJurisdictionQueries(data.jurisdiction.id);
      invalidateContractQueries();
    },
  });
};

export const useDeleteJurisdictionMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deleteJurisdictionV1ContractsJurisdictionsIdDelete({ id }),
        {
          loading: 'Deleting jurisdiction...',
          success: 'Jurisdiction deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      invalidateJurisdictionQueries(id);
      invalidateContractQueries();
    },
  });
};

export const usePartiesListQuery = (
  payload: GetPartiesV1ContractsPartiesGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetPartiesListResponse>({
    queryKey: [PARTIES_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPartiesV1ContractsPartiesGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePartiesMinimalListQuery = (
  payload: GetPartiesMinimalV1ContractsPartiesMinimalGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetPartiesMinimalListResponse>({
    queryKey: [PARTIES_MINIMAL_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPartiesMinimalV1ContractsPartiesMinimalGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePartyQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetPartyResponse>({
    queryKey: [PARTY_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPartyV1ContractsPartiesIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreatePartyMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreatePartyRequest) => {
      const response = await withErrorHandling(api.v1.createPartyV1ContractsPartiesPost(payload), {
        loading: 'Creating party...',
        success: 'Party created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      invalidatePartyQueries();
    },
  });
};

export const useUpdatePartyMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdatePartyBody }) => {
      const response = await withErrorHandling(
        api.v1.updatePartyV1ContractsPartiesIdPatch({ id }, data),
        {
          loading: 'Updating party...',
          success: 'Party updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      invalidatePartyQueries(id);
    },
  });
};

export const useArchivePartyMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deletePartyV1ContractsPartiesIdDelete({ id }),
        {
          loading: 'Archiving party...',
          success: 'Party archived',
        },
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      invalidatePartyQueries(id);
    },
  });
};

export const useRestorePartyMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.restorePartyV1ContractsPartiesIdRestorePost({ id }),
        {
          loading: 'Restoring party...',
          success: 'Party restored',
        },
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      invalidatePartyQueries(id);
    },
  });
};

export const usePartyContactsListQuery = (
  payload: GetPartyContactsV1ContractsPartyContactsGetParams = {},
  disabled = false,
) => {
  const { page, party_id, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetPartyContactsListResponse>({
    queryKey: [PARTY_CONTACTS_LIST_QUERY_KEY, party_id, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPartyContactsV1ContractsPartyContactsGet({
        ...data,
        party_id,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePartyNotesQuery = (
  payload: GetPartyNotesV1ContractsPartiesPartyIdNotesGetParams,
  disabled = false,
) => {
  const { partyId } = payload;
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetPartyNotesResponse>({
    queryKey: [PARTY_NOTES_QUERY_KEY, partyId, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPartyNotesV1ContractsPartiesPartyIdNotesGet({ partyId });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePartyNoteQuery = (
  payload: GetPartyNoteV1ContractsPartiesPartyIdNotesNoteIdGetParams,
  disabled = false,
) => {
  const { partyId, noteId } = payload;
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetPartyNoteResponse>({
    queryKey: [PARTY_NOTE_QUERY_KEY, partyId, noteId, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPartyNoteV1ContractsPartiesPartyIdNotesNoteIdGet({
        partyId,
        noteId,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePartyContactQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetPartyContactResponse>({
    queryKey: [PARTY_CONTACT_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPartyContactV1ContractsPartyContactsIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreatePartyContactMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreatePartyContactRequest) => {
      const response = await withErrorHandling(
        api.v1.createPartyContactV1ContractsPartyContactsPost(payload),
        {
          loading: 'Creating party contact...',
          success: 'Party contact created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidatePartyContactsQueries(data.party_contact.id, data.party_contact.party_id);
    },
  });
};

export const useUpdatePartyContactMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdatePartyContactBody }) => {
      const response = await withErrorHandling(
        api.v1.updatePartyContactV1ContractsPartyContactsIdPatch({ id }, data),
        {
          loading: 'Updating party contact...',
          success: 'Party contact updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidatePartyContactsQueries(data.party_contact.id, data.party_contact.party_id);
    },
  });
};

export const useDeletePartyContactMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeletePartyContactMutationPayload) => {
      const response = await withErrorHandling(
        api.v1.deletePartyContactV1ContractsPartyContactsIdDelete({ id: getPayloadId(payload) }),
        {
          loading: 'Deleting party contact...',
          success: 'Party contact deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, payload) => {
      invalidatePartyContactsQueries(
        getPayloadId(payload),
        typeof payload === 'number' ? undefined : payload.partyId,
      );
    },
  });
};

export const useCreatePartyNoteMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      partyId,
      data,
    }: CreatePartyNoteV1ContractsPartiesPartyIdNotesPostParams & {
      data: CreatePartyNoteBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.createPartyNoteV1ContractsPartiesPartyIdNotesPost({ partyId }, data),
        {
          loading: 'Creating party note...',
          success: 'Party note created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { partyId }) => {
      queryClient.invalidateQueries([PARTY_NOTES_QUERY_KEY, partyId]);
    },
  });
};

export const useDeletePartyNoteMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: DeletePartyNoteV1ContractsPartiesPartyIdNotesNoteIdDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deletePartyNoteV1ContractsPartiesPartyIdNotesNoteIdDelete(payload),
        {
          loading: 'Deleting party note...',
          success: 'Party note deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, { partyId }) => {
      queryClient.invalidateQueries([PARTY_NOTES_QUERY_KEY, partyId]);
    },
  });
};

export const useUpdatePartyNoteMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      partyId,
      noteId,
      data,
    }: UpdatePartyNoteV1ContractsPartiesPartyIdNotesNoteIdPatchParams & {
      data: UpdatePartyNoteBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.updatePartyNoteV1ContractsPartiesPartyIdNotesNoteIdPatch({ partyId, noteId }, data),
        {
          loading: 'Updating party note...',
          success: 'Party note updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { partyId }) => {
      queryClient.invalidateQueries([PARTY_NOTES_QUERY_KEY, partyId]);
    },
  });
};

export const useCompletePartyNoteFilesMutation = () => {
  return useMutation({
    mutationFn: async ({
      partyId,
      noteId,
      data,
    }: CompletePartyNoteFilesV1ContractsPartiesPartyIdNotesNoteIdCompleteFilesPostParams & {
      data: CompletePartyNoteFilesBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.completePartyNoteFilesV1ContractsPartiesPartyIdNotesNoteIdCompleteFilesPost(
          { partyId, noteId },
          data,
        ),
        {
          loading: 'Completing party note files...',
          success: 'Party note files completed',
        },
      );
      return response.data;
    },
    onSuccess: (_, { partyId }) => {
      queryClient.invalidateQueries([PARTY_NOTES_QUERY_KEY, partyId]);
    },
  });
};

export const useContractPartiesListQuery = (
  payload: GetContractPartiesV1ContractsContractPartiesGetParams = {},
  disabled = false,
) => {
  const { page, contract_id, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetContractPartiesListResponse>({
    queryKey: [CONTRACT_PARTIES_LIST_QUERY_KEY, contract_id, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getContractPartiesV1ContractsContractPartiesGet({
        ...data,
        contract_id,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useContractPartyQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetContractPartyResponse>({
    queryKey: [CONTRACT_PARTY_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getContractPartyV1ContractsContractPartiesIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreateContractPartyMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreateContractPartyRequest) => {
      const response = await withErrorHandling(
        api.v1.createContractPartyV1ContractsContractPartiesPost(payload),
        {
          loading: 'Creating contract party...',
          success: 'Contract party created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidateContractPartiesQueries(
        data.contract_party.id,
        data.contract_party.contract_id,
        data.contract_party.party_id,
      );
    },
  });
};

export const useUpdateContractPartyMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateContractPartyBody }) => {
      const response = await withErrorHandling(
        api.v1.updateContractPartyV1ContractsContractPartiesIdPatch({ id }, data),
        {
          loading: 'Updating contract party...',
          success: 'Contract party updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidateContractPartiesQueries(
        data.contract_party.id,
        data.contract_party.contract_id,
        data.contract_party.party_id,
      );
    },
  });
};

export const useDeleteContractPartyMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteContractPartyMutationPayload) => {
      const response = await withErrorHandling(
        api.v1.deleteContractPartyV1ContractsContractPartiesIdDelete({ id: getPayloadId(payload) }),
        {
          loading: 'Deleting contract party...',
          success: 'Contract party deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, payload) => {
      invalidateContractPartiesQueries(
        getPayloadId(payload),
        typeof payload === 'number' ? undefined : payload.contractId,
        typeof payload === 'number' ? undefined : payload.partyId,
      );
    },
  });
};

export const usePolicyCoveragesListQuery = (
  payload: GetPolicyCoveragesV1ContractsPolicyCoveragesGetParams = {},
  disabled = false,
) => {
  const { page, contract_id, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetPolicyCoveragesListResponse>({
    queryKey: [POLICY_COVERAGES_LIST_QUERY_KEY, contract_id, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPolicyCoveragesV1ContractsPolicyCoveragesGet({
        ...data,
        contract_id,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePolicyCoverageQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetPolicyCoverageResponse>({
    queryKey: [POLICY_COVERAGE_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPolicyCoverageV1ContractsPolicyCoveragesIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useCreatePolicyCoverageMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreatePolicyCoverageRequest) => {
      const response = await withErrorHandling(
        api.v1.createPolicyCoverageV1ContractsPolicyCoveragesPost(payload),
        {
          loading: 'Creating policy coverage...',
          success: 'Policy coverage created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidatePolicyCoveragesQueries(data.policy_coverage.id, data.policy_coverage.contract_id);
    },
  });
};

export const useUpdatePolicyCoverageMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdatePolicyCoverageBody }) => {
      const response = await withErrorHandling(
        api.v1.updatePolicyCoverageV1ContractsPolicyCoveragesIdPatch({ id }, data),
        {
          loading: 'Updating policy coverage...',
          success: 'Policy coverage updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: data => {
      invalidatePolicyCoveragesQueries(data.policy_coverage.id, data.policy_coverage.contract_id);
    },
  });
};

export const useDeletePolicyCoverageMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeletePolicyCoverageMutationPayload) => {
      const response = await withErrorHandling(
        api.v1.deletePolicyCoverageV1ContractsPolicyCoveragesIdDelete({
          id: getPayloadId(payload),
        }),
        {
          loading: 'Deleting policy coverage...',
          success: 'Policy coverage deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, payload) => {
      invalidatePolicyCoveragesQueries(
        getPayloadId(payload),
        typeof payload === 'number' ? undefined : payload.contractId,
      );
    },
  });
};

export const useContractFilesListQuery = (
  payload: GetContractFilesV1ContractsContractFilesGetParams = {},
  disabled = false,
) => {
  const { page, contract_id, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetContractFilesListResponse>({
    queryKey: [CONTRACT_FILES_LIST_QUERY_KEY, contract_id, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getContractFilesV1ContractsContractFilesGet({
        ...data,
        contract_id,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useContractsListQuery = (
  payload: GetContractsV1ContractsGetParams = {},
  disabled = false,
) => {
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetContractsListResponse>({
    queryKey: [CONTRACTS_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getContractsV1ContractsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useGenerateContractsExportZipMutation = () => {
  return useMutation({
    mutationFn: async (payload: GenerateContractExportZipRequest) => {
      const toastId = toast.loading('Preparing contracts ZIP...');
      try {
        const response = await api.instance.post<Blob>(`/v1/contracts/export/zip`, payload, {
          responseType: 'blob',
        });
        toast.success('Contracts ZIP generated', { id: toastId });

        return {
          blob: response.data,
          filename:
            typeof response.headers?.['x-filename'] === 'string' && response.headers['x-filename']
              ? response.headers['x-filename']
              : 'contracts.zip',
        };
      } catch (error) {
        toast.error(formatApiErrorMessage(error as AxiosError<any>), { id: toastId });
        throw error;
      }
    },
  });
};

export const useGenerateContractsExportReportMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: GenerateContractExportReportRequest & {
        format: ContractExportReportFormat;
      },
    ) => {
      const toastId = toast.loading(`Generating ${payload.format.toUpperCase()} report...`);
      try {
        const response = await api.instance.post<Blob>(`/v1/contracts/export/report`, payload, {
          responseType: 'blob',
        });
        toast.success(`${payload.format.toUpperCase()} report generated`, { id: toastId });

        return {
          blob: response.data,
          filename:
            typeof response.headers?.['x-filename'] === 'string' && response.headers['x-filename']
              ? response.headers['x-filename']
              : `contracts-report.${payload.format}`,
        };
      } catch (error) {
        toast.error(formatApiErrorMessage(error as AxiosError<any>), { id: toastId });
        throw error;
      }
    },
  });
};

export const useContractQuery = (id: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetContractResponse>({
    queryKey: [CONTRACT_QUERY_KEY, id, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60 * 3, //3 minutes
    queryFn: async () => {
      const response = await api.v1.getContractV1ContractsIdGet({ id });
      return response.data;
    },
    keepPreviousData: true,
    retry: 1,
    enabled: !disabled,
  });
};

export const useContractPartiesDetailsQuery = (contractId: number, disabled = false) => {
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetContractPartiesDetailsResponse>({
    queryKey: [CONTRACT_PARTIES_DETAILS_QUERY_KEY, contractId, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getContractPartiesDetailsV1ContractsContractIdPartiesGet({
        contractId,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useContractContributorsQuery = (
  payload: GetContractContributorsV1ContractsContractIdContributorsGetParams,
  disabled = false,
) => {
  const { contractId } = payload;
  const businessId = useGetCurrentBusinessId();

  return useQuery<GetContractContributorsResponse>({
    queryKey: [CONTRACT_CONTRIBUTORS_QUERY_KEY, contractId, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getContractContributorsV1ContractsContractIdContributorsGet({
        contractId,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useContractNotesQuery = (
  payload: GetContractNotesV1ContractsContractIdNotesGetParams,
  disabled = false,
) => {
  const { contractId, page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetContractNotesResponse>({
    queryKey: [CONTRACT_NOTES_QUERY_KEY, contractId, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getContractNotesV1ContractsContractIdNotesGet({
        ...data,
        contractId,
        page: pageParam || page,
      });
      return response.data;
    },
    getNextPageParam: ({ notes }) => {
      const totalPage = Math.ceil(notes.total / notes.page_size);
      const hasMore = totalPage > notes.page;
      if (hasMore) {
        return notes.page + 1;
      }
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useDeleteContractFileMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: DeleteContractFileByContractV1ContractsContractIdFilesFileIdDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deleteContractFileByContractV1ContractsContractIdFilesFileIdDelete(payload),
        {
          loading: 'Deleting contract file...',
          success: 'Contract file deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      invalidateContractFilesQueries();
      invalidateContractQueries();
    },
  });
};

export const useUpdateContractFileMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      fileId,
      data,
    }: UpdateContractFileV1ContractsContractIdFilesFileIdPatchParams & {
      data: UpdateContractFileBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.updateContractFileV1ContractsContractIdFilesFileIdPatch(
          { contractId, fileId },
          data,
        ),
        {
          loading: 'Updating contract file...',
          success: 'Contract file updated',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractQueries(contractId);
      invalidateContractFilesQueries();
    },
  });
};

export const useCreateContractMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async (payload: CreateContractRequest) => {
      const response = await withErrorHandling(api.v1.createContractV1ContractsPost(payload), {
        loading: 'Creating contract...',
        success: 'Contract created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: data => {
      invalidateContractQueries(data.contract.id);
      invalidateContractFilesQueries();
      invalidateContractPartiesQueries(undefined, data.contract.id);
    },
  });
};

export const useUpdateContractMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: UpdateContractV1ContractsIdPatchParams & { data: UpdateContractBody }) => {
      const response = await withErrorHandling(
        api.v1.updateContractV1ContractsIdPatch({ id }, data),
        {
          loading: 'Updating contract...',
          success: 'Contract updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      invalidateContractQueries(id);
    },
  });
};

export const useCreateContractContributorMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      contractId,
      data,
    }: CreateContractContributorV1ContractsContractIdContributorsPostParams & {
      data: CreateContractContributorBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.createContractContributorV1ContractsContractIdContributorsPost({ contractId }, data),
        {
          loading: 'Creating contract contributor...',
          success: 'Contract contributor created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractContributorsQueries(contractId);
      invalidateContractQueries(contractId);
    },
  });
};

export const useDeleteContractContributorMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      contributorId,
    }: DeleteContractContributorV1ContractsContractIdContributorsContributorIdDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteContractContributorV1ContractsContractIdContributorsContributorIdDelete({
          contractId,
          contributorId,
        }),
        {
          loading: 'Deleting contract contributor...',
          success: 'Contract contributor deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractContributorsQueries(contractId);
      invalidateContractQueries(contractId);
    },
  });
};

export const useUpsertContractContributorMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      contractId,
      contributorId,
      data,
    }: UpsertContractContributorV1ContractsContractIdContributorsContributorIdPatchParams & {
      data: UpsertContractContributorBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.upsertContractContributorV1ContractsContractIdContributorsContributorIdPatch(
          { contractId, contributorId },
          data,
        ),
        {
          loading: 'Updating contract contributor...',
          success: 'Contract contributor updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractContributorsQueries(contractId);
      invalidateContractQueries(contractId);
    },
  });
};

export const useArchiveContractMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.deleteContractV1ContractsIdDelete({ id }), {
        loading: 'Archiving contract...',
        success: 'Contract archived',
      });
      return response.data;
    },
    onSuccess: (_, id) => {
      invalidateContractQueries(id);
      invalidateContractFilesQueries();
      invalidateContractNotesQuery(id);
      invalidateContractPartiesQueries(undefined, id);
      invalidatePolicyCoveragesQueries(undefined, id);
    },
  });
};

export const useRestoreContractMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.restoreContractV1ContractsIdRestorePost({ id }),
        {
          loading: 'Restoring contract...',
          success: 'Contract restored',
        },
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      invalidateContractQueries(id);
      invalidateContractFilesQueries();
      invalidateContractNotesQuery(id);
      invalidateContractPartiesQueries(undefined, id);
      invalidatePolicyCoveragesQueries(undefined, id);
    },
  });
};

export const useCreateContractNoteMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      contractId,
      data,
    }: CreateContractNoteV1ContractsContractIdNotesPostParams & {
      data: CreateContractNoteBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.createContractNoteV1ContractsContractIdNotesPost({ contractId }, data),
        {
          loading: 'Creating contract note...',
          success: 'Contract note created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractNotesQuery(contractId);
    },
  });
};

export const useCompleteContractUploadMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      data,
    }: CompleteContractUploadV1ContractsContractIdCompleteUploadPostParams & {
      data: CompleteContractUploadBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.completeContractUploadV1ContractsContractIdCompleteUploadPost({ contractId }, data),
        {
          loading:
            'Completing contract upload and extracting contract details with AI, please wait...',
          success: 'Contract upload completed and extracted contract details with AI',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractQueries(contractId);
      invalidateContractFilesQueries();
      invalidatePolicyCoveragesQueries(undefined, contractId);
    },
  });
};

export const useInitContractFileUploadMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      data,
    }: InitContractFileUploadV1ContractsContractIdFilesInitUploadPostParams & {
      data: InitContractFileUploadBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.initContractFileUploadV1ContractsContractIdFilesInitUploadPost({ contractId }, data),
        {
          loading: 'Preparing contract files upload...',
          success: 'Contract files upload prepared',
        },
      );
      return response.data;
    },
  });
};

export const useCompleteContractFileUploadMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      data,
    }: CompleteContractFileUploadV1ContractsContractIdFilesCompleteUploadPostParams & {
      data: CompleteContractFileUploadBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.completeContractFileUploadV1ContractsContractIdFilesCompleteUploadPost(
          { contractId },
          data,
        ),
        {
          loading: 'Completing contract files upload...',
          success: 'Contract files uploaded',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractQueries(contractId);
      invalidateContractFilesQueries();
    },
  });
};

export const useDispatchContractForSigningMutation = () => {
  return useMutation({
    mutationFn: async (contractId: number) => {
      const response = await withErrorHandling(
        api.v1.dispatchContractForSigningV1ContractsContractIdSendForSigningPost({ contractId }),
        {
          loading: 'Sending contract for signing...',
          success: 'Contract sent for signing',
        },
      );
      return response.data;
    },
    onSuccess: (_, contractId) => {
      invalidateContractQueries(contractId);
      invalidateContractPartiesDetailsQuery(contractId);
    },
  });
};

export const useDeleteContractNoteMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteContractNoteV1ContractsContractIdNotesNoteIdDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteContractNoteV1ContractsContractIdNotesNoteIdDelete(payload),
        {
          loading: 'Deleting contract note...',
          success: 'Contract note deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractNotesQuery(contractId);
    },
  });
};

export const useUpdateContractNoteMutation = (
  setErrors?: (errors: FormikErrors<FormikValues>) => void,
) => {
  return useMutation({
    mutationFn: async ({
      contractId,
      noteId,
      data,
    }: UpdateContractNoteV1ContractsContractIdNotesNoteIdPatchParams & {
      data: UpdateContractNoteBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.updateContractNoteV1ContractsContractIdNotesNoteIdPatch(
          { contractId, noteId },
          data,
        ),
        {
          loading: 'Updating contract note...',
          success: 'Contract note updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractNotesQuery(contractId);
    },
  });
};

export const useDeleteContractNoteFileMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: DeleteContractNoteFileV1ContractsContractIdNotesNoteIdFilesStorageItemIdDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deleteContractNoteFileV1ContractsContractIdNotesNoteIdFilesStorageItemIdDelete(
          payload,
        ),
        {
          loading: 'Deleting contract note file...',
          success: 'Contract note file deleted',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractNotesQuery(contractId);
    },
  });
};

export const useCompleteContractNoteFilesMutation = () => {
  return useMutation({
    mutationFn: async ({
      contractId,
      noteId,
      data,
    }: CompleteContractNoteFilesV1ContractsContractIdNotesNoteIdCompleteFilesPostParams & {
      data: CompleteContractNoteFilesBody;
    }) => {
      const response = await withErrorHandling(
        api.v1.completeContractNoteFilesV1ContractsContractIdNotesNoteIdCompleteFilesPost(
          { contractId, noteId },
          data,
        ),
        {
          loading: 'Completing contract note files...',
          success: 'Contract note files completed',
        },
      );
      return response.data;
    },
    onSuccess: (_, { contractId }) => {
      invalidateContractNotesQuery(contractId);
    },
  });
};
